// supervisor-loop-post-route-attempt-state.ts
//
// Tách khỏi supervisor-loop-outcome-journal.ts (review 03a L6: file mẹ
// 198/200 dòng, 03b thêm `worker_decisions` E13). Ghi RetryState + bậc
// quota-backoff của ticket vào attempt-state.json sau khi route — chuyển
// nguyên văn, không đổi hành vi.
import type { RetryState } from "./retry-policy.js";
import {
  type AttemptStateFile,
  withQuotaBackoffIndex,
  withRetryState,
  writeAttemptStateFile,
} from "./supervisor-attempt-state.js";
import type { RouteOutcomeResult } from "./supervisor-outcome-router.js";

export interface PersistPostRouteAttemptStateInput {
  controlRoot: string;
  attemptState: AttemptStateFile;
  ticketId: string;
  attemptNo: number;
  priorRetryState: RetryState;
  priorBackoffIndex: number;
  route: RouteOutcomeResult;
}

/**
 * Writes the ticket's next RetryState + quota-backoff ladder position back
 * to attempt-state.json after a route decision. A quota_sleep outcome climbs
 * the ladder for THIS ticket; any OTHER outcome (success, retry, blocked,
 * escalate) ends the current sleep episode and resets the ladder back to
 * rung 0 — §18/§6.4 only specify "backoff 15→30→60" for ONE continuous run
 * of consecutive quota sleeps; once the ticket does a real (non-quota)
 * attempt, a LATER quota episode on the same ticket is a fresh occurrence,
 * not a continuation, and must not inherit the old ladder position (review
 * follow-up 260909-p6-m2, unresolved question 2).
 */
export async function persistPostRouteAttemptState(
  input: PersistPostRouteAttemptStateInput,
): Promise<void> {
  const { route, ticketId, attemptNo, priorRetryState, priorBackoffIndex } = input;
  // 06h R5c (dogfood 7 F-43, B2): the done path's `escalate()` hands the PRIOR
  // state back unchanged, so attemptNo would be reused next run (forensics dir
  // overwritten, `attempts_total` double-counted). Every route ran under
  // `attemptNo` ⇒ physical_runs can never be below it.
  const routed = route.kind === "retry_decision" ? route.decision.state : priorRetryState;
  const nextRetryState = { ...routed, physical_runs: Math.max(routed.physical_runs, attemptNo) };
  const withNextRetryState = withRetryState(input.attemptState, ticketId, nextRetryState);
  const nextAttemptState =
    route.kind === "quota_sleep"
      ? withQuotaBackoffIndex(withNextRetryState, ticketId, priorBackoffIndex + 1)
      : withQuotaBackoffIndex(withNextRetryState, ticketId, 0);
  await writeAttemptStateFile(input.controlRoot, nextAttemptState);
}
